/**
 * The Lab's named presets (the coordinator's item for the owner, Oct 10): a few setups the owner has played or asked to
 * test, shipped; and any Lab saved under a name of your own (this browser, `orbituary-lab-presets`). A preset sets rows
 * only — the Lab's defaults for the rest — and its seed pins the world, so a preset is the same match every time.
 * `?preset=name` (the name or its slug: `fog-valley`) expands into the preset's launch query before the page reads
 * its URL (game/urlPreset.ts); any other parameter given beside it wins.
 *
 * The shipped seeds were picked by a probe of the preset's own config (`createMatchState` over a few hundred seeds):
 * - **Fog valley** `valley-237`: the two teams 97 people apart either side of a dip 5 people deep with a few trees in
 *   it; rain at the start, fog rolling in at minute two, lifting by four (weather is a seeded timeline per planet).
 * - **Night forest** `night-44`: the site on the night side (the sun 0.55 below its horizon), a lantern grove near (382: forced, `biomeForce`).
 */
import { LAB_DEFAULTS, dropLegacy, labUrlParams, normalizeLabConfig, type LabConfig } from './labConfig';
import { LEGACY_PARAM } from './labRows';
import { STORY } from '../constants/action';

/** A row's value against another: by value (62.58: a per-team pair and a counts row are arrays and objects, never the same object). */
const same = (a: unknown, b: unknown): boolean => a === b || (typeof a === 'object' && a !== null && typeof b === 'object' && b !== null && JSON.stringify(a) === JSON.stringify(b));

export interface LabPreset { name: string; note: string; rows: Partial<LabConfig> }

export const LAB_PRESETS: readonly LabPreset[] = [
  {
    name: 'Fog valley',
    note: '10 v 10 either side of a thinly wooded valley; fog rolls in at minute two with the team picture on — the mid-valley skirmish the owner liked',
    rows: { mode: 'skirmish', seed: 'valley-237', planet: 'thalia', units: 10, stands: 'sparse', picture: true },
  },
  {
    name: 'SMG vs LMG',
    note: 'all SMG against all LMG, 8 a side (65.13 / 65.14, 67.24)',
    rows: { mode: 'skirmish', seed: 'lab-7', planet: 'thalia', units: 8, guns: ['smg', 'lmg'] },
  },
  {
    name: 'Night forest',
    note: 'night, biomes and dense stands, a lantern grove near (forced past the climate table, 382): the glowing plants and the shooting stars — dust for the first four minutes, then a clear sky (weather is always on since decision 147)',
    rows: { mode: 'skirmish', seed: 'night-44', planet: 'sonora', units: 6, stands: 'dense', biomeForce: 'lanternGrove' },
  },
  {
    name: 'Grenade range',
    note: 'three HE grenades and a smoke each, 4 a side, the teams 40 people apart',
    rows: { mode: 'skirmish', seed: 'lab-7', planet: 'thalia', units: 4, grenades: { he: 3, smoke: 1 }, front: '40' },
  },
  {
    // 69.5c (lane D's list): the slice under 69.6 — reserves and extraction left off on purpose (X19's arrivals land at the
    // side's start region, not by the push's landing, and the 69.6d × X19 rule is the owner's, unruled); 25 a side is the
    // same preset with units 25. The seed pins the world (a shipped preset's rule): fh-1, the board's measuring seed
    name: 'Foothold slice',
    note: 'every Foothold switch on (69.5c): two bases a quarter of the megaplanet apart, a push through 4 sectors, relays and FOBs, a 6×6 and a technical a side, 12 a side with the bot commanders and their plays',
    rows: { mode: 'skirmish', seed: 'fh-1', scale: 'human', planetSize: 'megaplanet', units: 12, board: '0.25', operations: 'push', sectors: 4, relays: true, fobs: true, transports: 1, technicals: 1, commanders: 'adaptive', commanderPlays: true },
  },
  {
    // 65.49 step 2: the owner, Oct 20: "I've been trying to ambush an enemy dropship for a while and can't do it"
    name: 'Dropship ambush',
    note: 'you carry the anti-dropship missile (65.49): the sides\' wave ships fly their sorties to the push\'s relays (the enemy\'s first lifts at ~1.5 min), ships destructible — hold it on an enemy ship until the lock, then fire; 8 a side on the quarter board',
    // (lane C's finding: with no forward point a wave ship has nowhere to fly — the push over its sectors and the relays give it one)
    rows: { mode: 'skirmish', seed: 'lab-7', scale: 'human', planetSize: 'megaplanet', units: 8, board: '0.25', operations: 'push', sectors: 4, relays: true, waveShips: true, shipsDestructible: true, loadout: 'rifle,missile,pistol' },
  },
  {
    // 69.60 (DECISIONS 270 (1)): the story's systems on together, on the board the owner plays (the full-size map, 69.8:
    // lab-7 on thalia, the open board at ½, the built ground) — the story objectives won by (69.50), the air-defence sites
    // and their generators, the munitions at the dumps with the rifle's reserves and a support in each squad (69.45), the
    // convoy's truck, relays and FOBs, a vehicle of each kind (the tracked carrier as the mortar) hit by zone (on by default),
    // and the mounted assault (69.37: the commanders call it). 69.61: the take of the high ground. 25 a side is units 25.
    // DECISIONS 297: on the large planet (radius ~130–270 people by seed), the ½ board kept; 390 (the owner: "megaplanets by
    // default"): on the megaplanet, the ⅛ board — the bases ~1,000 people apart (the ½ board on the large world: 540–840), and in
    // 390's survey the same story (first kills as soon, the objectives played out); the 1/16 board lays no objectives
    name: 'Story',
    note: 'every story system on (69.60): the open board at ⅛ on the megaplanet (390; ½ on the large planet before, 297), each side to hold the objective between the bases, to take the high ground in the other\'s half, to destroy the other\'s air-defence site and its generator, to upload from a point there, to carry its intel home (the wave ship fetches the carrier) and to escort a column of its vehicles to a point there (won by them, or by tickets, the most held at the end — not a held majority, which ended the story before it was told, nor an HQ: a fallen HQ is a setback, its side\'s tickets bleeding and its arrivals cut); munitions at the dumps, ammo reserves with a support in each squad, the convoy\'s truck, relays and FOBs; a 6×6, a technical, a tank and a mortar carrier a side, the wave ship and two dropship calls, the helicopter on a helipad behind each HQ (309) (ships can be downed: the sites keep them out until they fall); two HE grenades and a smoke each, more from the side\'s stock; the HQs tougher (600: a lone squad\'s grenades leave one standing); 12 a side with the bot commanders',
    rows: {
      mode: 'skirmish', seed: 'lab-7', planet: 'thalia', scale: 'human', planetSize: 'megaplanet', units: 12, board: '0.125', operations: 'open',   // (390: the megaplanet's ⅛ board — 297 kept the large planet's ½ because on the megaplanet's ½ the sides crossed the middle but rarely met)
      modularBuildings: true, terraces: true, settlements: true, regionDressing: true,
      collapse: true,   // (72.3 (c), DECISIONS 333/335: what loses its support comes down; a building's health is its modules', each round breaking what it reaches)
      storyObjectives: true, storyCarry: true, storyEscort: true, winObjectives: true, winMajority: false, winHq: false, airDefenceSites: true,   // (DECISIONS 294: the carry and the escort in the preset; their Lab rows turn either off)   // (the majority's 2 minutes ran with the middle's hold: it ended the story at 762 s)
      munitionsSupply: true, ammoReserves: true, supportRole: true, convoys: true, relays: true, fobs: true, vehicleStock: 1,
      transports: 1, technicals: 1, tanks: 1, tracked: 1, trackedArmament: 'mortar', mountedAssault: true,
      waveShips: true, dropships: '2', shipsDestructible: true,   // (69.61 step 3: ships for the sky the sites close — the wave ship's sorties and the air element, two calls a side; downed by the sites)
      helipad: true,   // (DECISIONS 309, the owner: "Story only" — 69.74: the side's helicopter parked on a pad behind its HQ, flown off it and back; the default skirmish keeps the off-map pool)
      skyHour: 'dusk',   // (74.2 step 0, DECISIONS 353 with §5.40's lean: the Story starts at dusk and ends in the dark — a told story's arc; any other match starts at a seeded hour)
      hqHealth: STORY.hqHealth,   // (DECISIONS 284 (a), "sure make it tougher": the HQ's fall a consequence, not the story's end — a lone squad's grenades leave it standing)
      // (DECISIONS 295, "Fall is a setback": no HQ win — on the ¼ board two pressed strikes felled one at 908 s with 4 of 12 objectives played and it ended the
      // match; now its side's tickets bleed (HQ_FALL) and its arrivals stop, and the objectives, the tickets and the time decide)
      grenades: { he: 2, smoke: 1 }, grenadeStock: true,   // (the story's strike sends only a squad with HE grenades: without them nobody went for a site; 69.61: from the side's stock, so a striking squad thrown out draws more at a crate or FOB)
      intelRecord: true, baseIntel: 'none',   // (DECISIONS 306, the owner: "On, nothing known" — 69.73: each side keeps its own record and its commander plans only on it,
      // nothing of the other's base known at the start; its strikes search the other's half until they find their targets)
    },
  },
];

export const LAB_PRESETS_KEY = 'orbituary-lab-presets';

/** A name's URL form: lower case, runs of anything but letters and digits as one hyphen. */
export function presetSlug(name: string): string {
  return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/** The rows of `c` that differ from the Lab's defaults (what a saved preset keeps). */
export function presetRows(c: LabConfig): Partial<LabConfig> {
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(LAB_DEFAULTS) as Array<keyof LabConfig>) if (!same(c[k], LAB_DEFAULTS[k])) out[k] = c[k];
  return out as Partial<LabConfig>;
}

/** Your saved presets, by name (empty when storage is unavailable or holds nothing readable). */
export function savedPresets(): Record<string, Partial<LabConfig>> {
  try {
    const v = JSON.parse(localStorage.getItem(LAB_PRESETS_KEY) ?? '{}');
    return v && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, Partial<LabConfig>> : {};
  } catch { return {}; }
}
function writeSaved(all: Record<string, Partial<LabConfig>>): void {
  try { localStorage.setItem(LAB_PRESETS_KEY, JSON.stringify(all)); } catch { /* storage unavailable */ }
}

/** Is `name` one of the shipped presets (by its slug)? */
export function isShippedPreset(name: string): boolean {
  const s = presetSlug(name);
  return LAB_PRESETS.some(p => presetSlug(p.name) === s);
}

/** Save `c` as `name`: false for an empty name or a shipped preset's (those stay as shipped). */
export function savePreset(name: string, c: LabConfig): boolean {
  const n = name.trim().slice(0, 40);
  if (!presetSlug(n) || isShippedPreset(n)) return false;
  const all = savedPresets();
  for (const k of Object.keys(all)) if (presetSlug(k) === presetSlug(n)) delete all[k];   // one per slug: saving again replaces it
  all[n] = presetRows(c);
  writeSaved(all);
  return true;
}

export function deletePreset(name: string): void {
  const all = savedPresets();
  for (const k of Object.keys(all)) if (presetSlug(k) === presetSlug(name)) delete all[k];
  writeSaved(all);
}

/** Every preset's name: the shipped ones, then yours. */
export function presetNames(): string[] {
  return [...LAB_PRESETS.map(p => p.name), ...Object.keys(savedPresets()).filter(n => !isShippedPreset(n))];
}

/** The Lab a preset sets (by its name or slug; the shipped ones first), or null for none. */
export function presetConfig(name: string): LabConfig | null {
  const s = presetSlug(name);
  if (!s) return null;
  const shipped = LAB_PRESETS.find(p => presetSlug(p.name) === s);
  if (shipped) return normalizeLabConfig({ ...LAB_DEFAULTS, presentationDials: undefined, ...shipped.rows });   // (62.58: the master as the rows say, unless they name it)
  const saved = Object.entries(savedPresets()).find(([k]) => presetSlug(k) === s);
  return saved ? normalizeLabConfig({ ...LAB_DEFAULTS, presentationDials: undefined, ...saved[1] }) : null;
}

/** The name of the preset `c` is exactly (shipped first), or null. */
export function presetOf(c: LabConfig): string | null {
  for (const name of presetNames()) {
    const p = presetConfig(name);
    if (p && (Object.keys(LAB_DEFAULTS) as Array<keyof LabConfig>).every(k => same(p[k], c[k]))) return name;
  }
  return null;
}

/**
 * A page query with `preset=…` expanded into the preset's launch query (labUrlParams), every other parameter given
 * beside it laid over; null when the query names no preset or one that is not known.
 */
export function expandPreset(search: string): string | null {
  const given = new URLSearchParams(search);
  const name = given.get('preset');
  if (name === null) return null;
  const c = presetConfig(name);
  if (!c) return null;
  const out = labUrlParams(given.get(LEGACY_PARAM) === '1' ? c : dropLegacy(c));   // (69.13 (d): a preset's retired rows only beside legacy=1)
  for (const [k, v] of given) if (k !== 'preset') out.set(k, v);
  return '?' + out.toString().replace(/=(?=&|$)/g, '');
}
